/**
 * useEvidenceChipsConfig - Manages evidence chips configuration state and handlers
 *
 * This hook encapsulates the server config integration for evidence chips,
 * including enabled/disabled state, enabled categories, and pinned queries.
 *
 * Categories are stored in the backend's `enabled_categories` field and can be
 * toggled on/off via the suggestions button UI.
 */

import type { PinnedChip } from '@/components/ui/floating-ask-heidi';
import { MAX_SHORTCUTS_LIMIT } from '@/components/ui/floating-ask-heidi/shortcut-constants';
import type { SuggestedChipCategory } from '@/components/ui/transcript-chips/types';
import { useMutateEvidenceConfig } from '@/hooks/mutation/useMutateEvidenceConfig';
import {
  DEFAULT_EVIDENCE_CONFIG,
  useQueryEvidenceConfig,
} from '@/hooks/query/useQueryEvidenceConfig';
import type { EffectivePinnedQuery, PinnedQuery } from '@/lib/api/transcript-chips';
import { isHeidiSupportReachable } from '@/utils/heidiReach';
import { formatMessageOutsideReact } from '@/utils/i18n/intl-bridge';
import { toast } from '@heidi/design-system/toast';
import { useTranslations } from '@heidi/runtime/i18n';
import { useCallback, useMemo, useState } from 'react';
import { defineMessages } from 'react-intl';

const messages = defineMessages({
  saveShortcutErrorContactIt: {
    defaultMessage: 'Please try again or contact your IT team.',
    id: 'lGDDAdbAR4',
  },
});

/**
 * Convert an EffectivePinnedQuery from server to PinnedChip for UI
 */
const effectivePinnedQueryToChip = (pq: EffectivePinnedQuery): PinnedChip => ({
  id: pq.id,
  label: pq.label,
  query: pq.query,
  description: pq.description || undefined,
  category: pq.category ?? 'plan',
  isPermanent: true,
  isDefault: pq.is_default,
  teamShortcutId: pq.team_shortcut_id,
  origin: pq.origin,
});

const chipToPinnedQuery = (chip: PinnedChip): PinnedQuery => ({
  label: chip.label,
  query: chip.query,
  ...(chip.description?.trim() ? { description: chip.description.trim() } : {}),
  // Preserve each existing pin's origin so re-saving the list doesn't silently
  // re-classify older entries. Pins whose origin is unknown/legacy fall
  // through to the server's "custom" default.
  ...(chip.origin === 'library' || chip.origin === 'custom'
    ? { origin: chip.origin }
    : {}),
});

export interface EvidenceChipsConfigState {
  /** Whether suggestions are enabled globally */
  suggestEnabled: boolean;
  /** Set of enabled chip categories */
  enabledCategories: Set<SuggestedChipCategory>;
  /** Pinned chips from server config */
  pinnedChips: PinnedChip[];
  /** Whether auto-answer for pinned chips is enabled (local state) */
  autoAnswerPinned: boolean;
  /** Current number of shortcuts (pinned chips) */
  shortcutCount: number;
  /** Maximum number of shortcuts allowed */
  maxShortcuts: number;
  /** Whether the user has reached the shortcut limit */
  isAtShortcutLimit: boolean;
  /**
   * FDE-8: user-level toggle for library-grounded chip suggestions. `true` by
   * default, but a user can opt out regardless of team / Flagsmith gating.
   */
  libraryGroundingEnabled: boolean;
}

export interface EvidenceChipsConfigHandlers {
  /** Handler for toggling suggest enabled */
  handleSuggestEnabledChange: (enabled: boolean) => void;
  /** Handler for toggling a category on/off */
  handleCategoryToggle: (category: SuggestedChipCategory) => void;
  /** Handler for toggling auto-answer pinned */
  handleAutoAnswerPinnedChange: (enabled: boolean) => void;
  /**
   * Handler for adding a personal permanent chip. Team shortcuts are
   * auto-pinned server-side and cannot be added via this handler. Pass the
   * origin so analytics can distinguish marketplace installs ('library') from
   * user-authored shortcuts ('custom'); existing pins retain their stored
   * origin.
   */
  handleAddPermanentChip: (
    label: string,
    query: string,
    origin: 'library' | 'custom',
    description?: string
  ) => void;
  /** Handler for editing an existing permanent chip */
  handleEditPermanentChip: (
    chipId: string,
    label: string,
    query: string,
    description?: string
  ) => void;
  /** Handler for removing a permanent chip */
  handleRemovePermanentChip: (chipId: string) => void;
  /** Handler for restoring a dismissed default shortcut */
  handleRestoreDefaultShortcut: (shortcutId: string) => void;
  /** FDE-8: toggle library-grounded chip suggestions for this user */
  handleLibraryGroundingEnabledChange: (enabled: boolean) => void;
}

export type UseEvidenceChipsConfigReturn = EvidenceChipsConfigState &
  EvidenceChipsConfigHandlers;

export function useEvidenceChipsConfig(): UseEvidenceChipsConfigReturn {
  const { data: serverConfig } = useQueryEvidenceConfig();
  const { mutate: updateConfig } = useMutateEvidenceConfig();
  const { t } = useTranslations();

  // Derive state directly from React Query cache (single source of truth)
  // This prevents race conditions between local state and server sync
  const suggestEnabled = serverConfig?.enabled ?? true;
  const libraryGroundingEnabled = serverConfig?.library_grounding_enabled ?? true;

  const pinnedChips = useMemo(
    () => serverConfig?.pinned_queries?.map(effectivePinnedQueryToChip) ?? [],
    [serverConfig?.pinned_queries]
  );

  // Get enabled categories from server config, falling back to defaults
  const enabledCategories = useMemo(() => {
    const categories =
      serverConfig?.enabled_categories ?? DEFAULT_EVIDENCE_CONFIG.enabled_categories;
    return new Set<SuggestedChipCategory>(categories);
  }, [serverConfig?.enabled_categories]);

  // autoAnswerPinned is local-only (no server config yet)
  const [autoAnswerPinned, setAutoAnswerPinned] = useState(false);

  // Handlers only call mutation - optimistic updates handled by React Query
  const handleSuggestEnabledChange = useCallback(
    (enabled: boolean) => {
      updateConfig({ enabled });
    },
    [updateConfig]
  );

  // Toggle a category on/off - updates the enabled_categories in the backend
  const handleCategoryToggle = useCallback(
    (category: SuggestedChipCategory) => {
      const currentCategories = serverConfig?.enabled_categories ?? [];
      const isEnabled = currentCategories.includes(category);

      const newCategories = isEnabled
        ? currentCategories.filter((c) => c !== category)
        : [...currentCategories, category];

      updateConfig({ enabled_categories: newCategories });
    },
    [serverConfig?.enabled_categories, updateConfig]
  );

  const handleAutoAnswerPinnedChange = useCallback((enabled: boolean) => {
    setAutoAnswerPinned(enabled);
    // Note: auto_answer_pinned is stored locally for now
    // Add to updateConfig when backend supports it
  }, []);

  const showShortcutError = useCallback(() => {
    toast({
      tone: 'destructive',
      title: t('floatingAskHeidiShortcuts.saveShortcutError'),
      description: isHeidiSupportReachable
        ? t('floatingAskHeidiShortcuts.saveShortcutErrorDescription')
        : formatMessageOutsideReact(messages.saveShortcutErrorContactIt),
    });
  }, [t]);

  const shortcutCount = pinnedChips.length;
  const isAtShortcutLimit = shortcutCount >= MAX_SHORTCUTS_LIMIT;

  const handleAddPermanentChip = useCallback(
    (
      label: string,
      query: string,
      origin: 'library' | 'custom',
      description?: string
    ) => {
      if (isAtShortcutLimit) return;

      // Team shortcuts are auto-pinned server-side — the "add personal pin"
      // path here only handles personal shortcuts. Any team chips in the
      // current list are not persisted back on save. chipToPinnedQuery
      // preserves each remaining pin's origin so analytics classifications
      // survive a re-save.
      const personalPins = pinnedChips
        .filter((chip) => !chip.isDefault && !chip.teamShortcutId)
        .map(chipToPinnedQuery);

      const trimmedDesc = description?.trim();
      const newPins = [
        ...personalPins,
        {
          label,
          query,
          origin,
          ...(trimmedDesc ? { description: trimmedDesc } : {}),
        },
      ];
      updateConfig({ pinned_queries: newPins }, { onError: showShortcutError });
    },
    [pinnedChips, updateConfig, showShortcutError, isAtShortcutLimit]
  );

  const handleEditPermanentChip = useCallback(
    (chipId: string, label: string, query: string, description?: string) => {
      // Team chips are admin-managed; reject edits here instead of silently
      // turning them into personal pins.
      const target = pinnedChips.find((c) => c.id === chipId);
      if (target?.teamShortcutId) return;

      const trimmedDesc = description?.trim();
      // Edits only apply to personal pins — team shortcuts are admin-managed
      // (rejected above). Preserve each chip's existing origin across edits —
      // we're editing content, not re-categorising where the shortcut came
      // from.
      const personalPins = pinnedChips
        .filter((chip) => !chip.isDefault && !chip.teamShortcutId)
        .map((chip) => {
          const originPayload =
            chip.origin === 'library' || chip.origin === 'custom'
              ? { origin: chip.origin }
              : {};
          if (chip.id === chipId) {
            return {
              label,
              query,
              ...(trimmedDesc ? { description: trimmedDesc } : {}),
              ...originPayload,
            };
          }
          return chipToPinnedQuery(chip);
        });
      updateConfig({ pinned_queries: personalPins }, { onError: showShortcutError });
    },
    [pinnedChips, updateConfig, showShortcutError]
  );

  const handleRemovePermanentChip = useCallback(
    (chipId: string) => {
      const chipToRemove = pinnedChips.find((c) => c.id === chipId);
      if (!chipToRemove) return;

      // Team shortcuts are admin-managed and cannot be removed by members.
      if (chipToRemove.teamShortcutId) return;

      if (chipToRemove.isDefault) {
        const dismissedIds = serverConfig?.dismissed_default_ids ?? [];
        updateConfig({ dismissed_default_ids: [...dismissedIds, chipId] });
      } else {
        // For personal pins, update the pinned_queries list. Team shortcuts
        // are rejected above, and chipToPinnedQuery preserves each remaining
        // pin's origin so existing classifications survive a removal.
        const remainingPersonalPins = pinnedChips
          .filter((chip) => !chip.isDefault && !chip.teamShortcutId && chip.id !== chipId)
          .map(chipToPinnedQuery);
        updateConfig({ pinned_queries: remainingPersonalPins });
      }
    },
    [pinnedChips, serverConfig, updateConfig]
  );

  const handleRestoreDefaultShortcut = useCallback(
    (shortcutId: string) => {
      if (isAtShortcutLimit) return;

      const dismissedIds = serverConfig?.dismissed_default_ids ?? [];
      const newDismissedIds = dismissedIds.filter((id) => id !== shortcutId);
      updateConfig({ dismissed_default_ids: newDismissedIds });
    },
    [serverConfig, updateConfig, isAtShortcutLimit]
  );

  const handleLibraryGroundingEnabledChange = useCallback(
    (enabled: boolean) => {
      updateConfig({ library_grounding_enabled: enabled });
    },
    [updateConfig]
  );

  return {
    // State
    suggestEnabled,
    enabledCategories,
    pinnedChips,
    autoAnswerPinned,
    shortcutCount,
    maxShortcuts: MAX_SHORTCUTS_LIMIT,
    isAtShortcutLimit,
    libraryGroundingEnabled,
    // Handlers
    handleSuggestEnabledChange,
    handleCategoryToggle,
    handleAutoAnswerPinnedChange,
    handleAddPermanentChip,
    handleEditPermanentChip,
    handleRemovePermanentChip,
    handleRestoreDefaultShortcut,
    handleLibraryGroundingEnabledChange,
  };
}
